<template>
  <div v-if="school.concurrencyRV">
    <div class="q-pa-md q-mx-auto" style="max-width: 1000px">
      <h6 class="text-center" v-if="userName">
        {{ userName }} - {{ userRoleToName(store.state.user) }} Dashboard
      </h6>
      <CSCard style="width: 100%">
        <CSCardSection>
          <p class="text-center text-h6">Participating School Status</p>
          <div class="row q-my-sm">
            <p>
              Complete the following steps to become a participating school.
              This will allow you to submit funding requests as per Help A
              Family’s
              <RouterLink to="/" @click="showDialog = true">
                Terms and Conditions
              </RouterLink>
              .
            </p>
            <p>
              Invite other school officers to participate by selecting “School
              Officers” from the menu.
            </p>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              Principal Registered:
            </span>
            <span class="col">
              <ItemCrossCheck
                :is-positive="
                  principalAccount.schoolOfficerDeclarationCd === 'SOD_A'
                "
              />
            </span>
          </div>
          <div
            class="row q-my-sm"
            v-if="principalAccount.schoolOfficerDeclarationCd !== 'SOD_A'"
          >
            <span class="text-negative">
              Ensure you have invited the School Principal and they complete
              their registration process using the “School Officers” menu above.
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              Principal Financially Active:
            </span>
            <span class="col">
              <ItemCrossCheck :is-positive="isCMFinancial(principalAccount)" />
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              Admin Registered:
            </span>
            <span class="col">
              <ItemCrossCheck
                :is-positive="
                  adminAccount.schoolOfficerDeclarationCd === 'SOD_A'
                "
              />
            </span>
          </div>
          <div
            class="row q-my-sm"
            v-if="adminAccount.schoolOfficerDeclarationCd !== 'SOD_A'"
          >
            <span class="text-negative">
              Ensure you have invited the School Admin and they complete their
              registration process using the “School Officers” menu above.
            </span>
          </div>

          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              Admin Financially Active:
            </span>
            <span class="col">
              <ItemCrossCheck :is-positive="isCMFinancial(adminAccount)" />
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              School Bank Account:
            </span>
            <span class="col">
              <ItemCrossCheck :is-positive="school.hasBankDetailsInd" />
            </span>
          </div>
          <div class="row q-my-sm" v-if="!school.hasBankDetailsInd">
            <span class="text-negative">
              This the responsibility of the School Principal during their
              registration.
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              Participating School:
            </span>
            <span class="col">
              <ItemCrossCheck
                :is-positive="school.schoolStatusCd === SchoolStatusEnum.SS_Pa"
              />
            </span>
          </div>
          <div
            class="row q-my-sm"
            v-if="school.schoolStatusCd === SchoolStatusEnum.SS_AD"
          >
            <span class="text-negative">
              Your school has been deactivated by Help A Family. Please contact
              Help A Family Administration
            </span>
          </div>
          <q-separator class="q-mb-sm" />
          <p class="text-center text-h6">{{ school.name }}</p>
          <p
            class="text-center text-body1"
            v-if="schoolOfficerRole === 'SOR_P'"
          >
            Your school has now
            <strong>{{ formatMonetaryAmt(school.accountBalance) }}</strong>
            available funding balance
          </p>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column"> School Status: </span>
            <span class="col">
              {{
                mapReferenceCodeToName(
                  store.state.referenceData.schoolStatuses,
                  school.schoolStatusCd,
                )
              }}
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column"> ACARAId: </span>
            <span class="col">
              {{ school.acaraId ?? "Unknown" }}
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column"> Student Count: </span>
            <span class="col">
              {{ school.approxStudentsNum }}
            </span>
          </div>
          <div v-for="item in school.schoolAddresses" class="row q-my-sm">
            <span class="text-weight-medium left-column">
              {{
                mapReferenceCodeToName(
                  store.state.referenceData.addressTypes,
                  item.addressTypeCd,
                )
              }}
              Address:
            </span>
            <span class="col">
              {{ formatAddress(item) }}
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column"> Phone: </span>
            <span class="col">
              {{ formatPhoneNumber(school.phoneNumber) }}
            </span>
          </div>
          <div class="row q-my-sm">
            <span class="text-weight-medium left-column">
              School Offficers:
            </span>
            <ul>
              <li v-for="item in school.schoolOfficers">
                {{ item.givenName }} {{ item.familyName }} -
                {{
                  mapReferenceCodeToName(
                    store.state.referenceData.schoolOfficerRoles,
                    item.schoolOfficerRoleCd,
                  )
                }}
              </li>
            </ul>
          </div>
        </CSCardSection>
      </CSCard>
    </div>
    <p v-if="accountStatus === 'US_I'">Your account is currently inactive</p>
  </div>

  <CSDialog v-model="showDialog">
    <CSCard>
      <CSCardSection class="row">
        <CSIcon
          name="fa-solid fa-file-signature"
          size="24px"
          color="primary"
          class="self-center q-pa-sm"
        />
        <div class="self-center text-h6">Terms and Conditions</div>
      </CSCardSection>

      <q-separator />

      <CSCardSection style="max-height: 50vh" class="scroll">
        <span
          v-html="
            getDeclaration(
              store.state.user?.userRoleCd as SchoolOfficerRoleEnum | undefined,
            )
          "
        ></span>
      </CSCardSection>

      <q-separator />

      <CSCardActions align="right">
        <CSButton flat label="Ok" @click="showDialog = false" />
      </CSCardActions>
    </CSCard>
  </CSDialog>
</template>

<script setup lang="ts">
import CSCard from "@/components/CharitySystem/CSCard.vue";
import CSCardSection from "@/components/CharitySystem/CSCardSection.vue";
import CSCardActions from "@/components/CharitySystem/CSCardActions.vue";
import CSDialog from "@/components/CharitySystem/CSDialog.vue";
import CSIcon from "@/components/CharitySystem/CSIcon.vue";
import ItemCrossCheck from "@/components/ItemCrossCheck.vue";
import { showErrorNotification } from "@/components/shared/dialog";
import SchoolDetailsModel from "@/models/ViewModels/SchoolDetailModel";
import SchoolOfficerViewModel from "@/models/ViewModels/SchoolOfficerModel";
import {
  formatAddress,
  formatMonetaryAmt,
  formatPhoneNumber,
  mapReferenceCodeToName,
  processResponse,
  userRoleToName,
} from "@/shared/helperFunctions";
import store from "@/store";
import SchoolOfficerUseCase from "@/usecases/SchoolOfficerUseCase";
import SchoolUseCase from "@/usecases/SchoolUseCase";
import { useQuasar } from "quasar";
import { SchoolOfficerRoleEnum, SchoolStatusEnum } from "@/shared/ModelEnums";

import { onMounted, reactive, ref, computed } from "vue";
import { useRoute } from "vue-router";
import CSButton from "@/components/CharitySystem/CSButton.vue";
import { createApiException } from "@/api/ResponseModel";

const $q = useQuasar();

const route = useRoute();

const school = reactive(new SchoolDetailsModel());

const accountStatus = ref("");

const showDialog = ref(false);

const schoolOfficerRole = ref<string | null>(null);

const principalAccount = computed(
  () =>
    school.schoolOfficers.find(
      (item) => item.schoolOfficerRoleCd === SchoolOfficerRoleEnum.SOR_P,
    ) ?? new SchoolOfficerViewModel(),
);

const adminAccount = computed(
  () =>
    school.schoolOfficers.find(
      (item) => item.schoolOfficerRoleCd === SchoolOfficerRoleEnum.SOR_A,
    ) ?? new SchoolOfficerViewModel(),
);

const userName = computed(() => {
  const user = store.state.user;
  return user ? `${user.givenName} ${user.familyName}` : "";
});

onMounted(async () => {
  try {
    const userReference = store.state.user?.userReference;
    const reference = route.params.reference as string;
    if (reference) {
      const schoolUseCase = new SchoolUseCase();
      const result = await schoolUseCase.getSchoolByReference(reference);
      processResponse(result, (content) => Object.assign(school, content));
    } else if (userReference) {
      const schoolOfficerUseCase = new SchoolOfficerUseCase();
      const schoolOfficer =
        await schoolOfficerUseCase.getByReference(userReference);

      processResponse(schoolOfficer, async (officer) => {
        accountStatus.value = officer.userStatusCd;
        schoolOfficerRole.value = officer.schoolOfficerRoleCd;
        const schoolUseCase = new SchoolUseCase();
        const result = await schoolUseCase.getSchoolByReference(
          officer.schoolReference,
        );
        processResponse(result, (content) => Object.assign(school, content));
      });
    } else {
      throw createApiException(
        "No Reference Provided",
        "School reference is required to fetch school details.",
      );
    }
  } catch (ex) {
    if (ex instanceof Error) $q.notify(showErrorNotification(ex.message));
  }
});

function isCMFinancial(schoolOfficer: SchoolOfficerViewModel) {
  const committeeMember = schoolOfficer.administrationPayments.find(
    (item) => item.administrationPayerTypeCd === "APT_SO",
  );
  if (!committeeMember?.nextChargeDT) return false;
  else {
    if (new Date() > new Date(committeeMember.nextChargeDT)) return false;
    else return true;
  }
}

function getDeclaration(userRole?: SchoolOfficerRoleEnum) {
  const systemSetting = store.state.systemSetting;
  if (systemSetting) {
    switch (userRole) {
      case SchoolOfficerRoleEnum.SOR_P:
        return systemSetting.schoolPrincipalTermsAndConditions;
      case SchoolOfficerRoleEnum.SOR_A:
        return systemSetting.schoolAdministratorTermsAndConditions;
      case SchoolOfficerRoleEnum.SOR_S:
        return systemSetting.schoolOfficerStaffTermsAndConditions;
      case SchoolOfficerRoleEnum.SOR_O:
        return systemSetting.schoolOfficerOtherTermsAndConditions;
      default:
        return `${userRole} Terms and Conditions not found`;
    }
  } else {
    return "System Setting not loaded";
  }
}
</script>

<style lang="scss" scoped>
.left-column {
  width: 200px;
}
</style>
